import {EventEmitter, Injectable} from '@angular/core';
import {BusinessDataService} from '../../../data-repositories/business-data.service';
import {User} from '../../../models/user';
import {Stage} from '../../../models/stage';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {AccountType} from '../../../models/account-type';
import {take} from 'rxjs/operators';
import {IEmploymentInfo} from '../../../models/employment-info';

@Injectable({
  providedIn: 'root'
})
export class StagesNavigationService {
  public onChange = new EventEmitter();
  private _userModel: User;

  constructor(
    service: BusinessDataService,
    router: Router,
    route: ActivatedRoute) {
    service.GetUser().subscribe(u => {
      this._userModel = u.model;
      this.updateCurrentTab(route);
    });

    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateCurrentTab(route);
      }
    });
  }

  private _currentTab: Stage;

  get currentTab(): Stage {
    return this._currentTab;
  }

  private _currentTabIndex: number;

  get currentTabIndex(): number {
    return this._currentTabIndex;
  }

  private _currentDisplayTabIndex: number;

  get currentDisplayTabIndex(): number {
    return this._currentDisplayTabIndex;
  }

  private _workflowStages: Stage[] = [
    {
      name: 'PurposeType',
      stageOutlet: 'purpose-type',
      applicationStatus: 'AccountType',
      isGroup: false,
      canNavigate: () => this.isMobile,
      showInProgress: () => false,
      mobileGroup: 0,
      group: 1,
      step: 1
    },
    {
      name: 'AccountType',
      stageOutlet: 'account-type',
      applicationStatus: 'AccountType',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => false,
      mobileGroup: 0,
      group: 1,
      step: 1
    },
    // {
    //   name: 'JointInfo',
    //   stageOutlet: 'joint-info',
    //   applicationStatus: 'AccountType',
    //   isGroup: false,
    //   canNavigate: () => this.isMobile && this.accountTypeIs(this._userModel, AccountType.Joint, false),
    //   showInProgress: () => false,
    //   splash: true,
    //   mobileGroup: 0
    // },
    {
      name: 'Personal',
      stageOutlet: 'personal',
      applicationStatus: 'PersonalInfo',
      isGroup: false,
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Single),
      showInProgress: () => true,
      mobileGroup: 0,
      group: 1,
      step: 1
    },
    {
      name: 'Address',
      stageOutlet: 'address',
      applicationStatus: 'AddressInfo',
      isGroup: false,
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Single),
      showInProgress: () => true,
      mobileGroup: 0,
      group: 1,
      step: 2
    },
    {
      name: 'Employment',
      stageOutlet: 'employment',
      applicationStatus: 'EmploymentInfo',
      isGroup: false,
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Single),
      showInProgress: () => true,
      mobileGroup: 0,
      group: 1,
      step: 3
    },

    {
      name: 'Personal',
      stageOutlet: 'personal-first-holder',
      applicationStatus: 'PersonalInfoFirst',
      isGroup: true,
      groupId: 0,
      groupName: 'FirstHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 0,
      group: 1,
      step: 1
    },
    {
      name: 'Address',
      stageOutlet: 'address-first-holder',
      applicationStatus: 'AddressInfoFirst',
      isGroup: true,
      groupId: 0,
      groupName: 'FirstHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 0,
      group: 1,
      step: 1
    },
    {
      name: 'Employment',
      stageOutlet: 'employment-first-holder',
      applicationStatus: 'EmploymentInfoFirst',
      isGroup: true,
      isDelimiter: true,
      groupId: 0,
      groupName: 'FirstHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 0,
      group: 1,
      step: 1
    },
    {
      name: 'PersonalInfoCompleteFirst',
      stageOutlet: 'personal-info-complete-first-holder',
      isGroup: false,
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => false,
      splash: true,
      mobileGroup: 1,
      group: 1,
      step: 1
    },    
    {
      name: 'Personal',
      stageOutlet: 'personal-second-holder',
      applicationStatus: 'PersonalInfoSecond',
      isGroup: true,
      groupId: 1,
      groupName: 'SecondHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 1,
      group: 1,
      step: 2
    },
    {
      name: 'Address',
      stageOutlet: 'address-second-holder',
      applicationStatus: 'AddressInfoSecond',
      isGroup: true,
      groupId: 1,
      groupName: 'SecondHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 1,
      group: 1,
      step: 2
    },
    {
      name: 'Employment',
      stageOutlet: 'employment-second-holder',
      applicationStatus: 'EmploymentInfoSecond',
      isGroup: true,
      isDelimiter: true,
      groupId: 1,
      groupName: 'SecondHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 1,
      group: 1,
      step: 2
    },

    {
      name: 'PersonalInfoComplete',
      stageOutlet: 'personal-info-complete',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => false,
      splash: true,
      mobileGroup: 1,
      group: 1,
      step: 1
    },

    {
      name: 'AssetsAndIncome',
      stageOutlet: 'experience',
      applicationStatus: 'AssetsInfo',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => true,
      mobileGroup: 2,
      group: 2,
      step: 3
    },
    {
      name: 'Investment',
      stageOutlet: 'investment',
      applicationStatus: 'InvestmentInfo',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => true,
      mobileGroup: 2,
      group: 2,
      step: 4
    },
    {
      name: 'SourceOfWealth',
      stageOutlet: 'source-of-wealth',
      applicationStatus: 'SourceOfWealth',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => true,
      mobileGroup: 3,
      group: 2,
      step: 5
    },

    {
      name: 'ExperienceInfoComplete',
      stageOutlet: 'experience-info-complete',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => false,
      splash: true,
      mobileGroup: 3,
      group: 2,
      step: 1
    },

    {
      name: 'RegInfo',
      stageOutlet: 'regulatory',
      applicationStatus: 'RegInfo',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => true,
      mobileGroup: 4,
      group: 3,
      step: 6
    },
    {
      name: 'W8',
      stageOutlet: 'substitute',
      applicationStatus: 'SubstituteInfo',
      isGroup: false,
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Single),
      showInProgress: () => true,
      mobileGroup: 4,
      group: 3,
      step: 7
    },

    {
      name: 'W8',
      stageOutlet: 'substitute-first-holder',
      applicationStatus: 'SubstituteInfoFirst',
      isGroup: true,
      groupId: 2,
      groupName: 'FirstHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 4,
      group: 3,
      step: 7
    },

    {
      name: 'W8',
      stageOutlet: 'substitute-second-holder',
      applicationStatus: 'SubstituteInfoSecond',
      isGroup: true,
      groupId: 3,
      isDelimiter: true,
      groupName: 'SecondHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 4,
      group: 3,
      step: 7
    },

    {
      name: 'GeneralAgreements',
      stageOutlet: 'general-agreement',
      applicationStatus: 'GeneralAgreementsInfo',
      isGroup: false,
      canNavigate: () => true,
      showInProgress: () => true,
      requiresServerValidation: true,
      mobileGroup: 5,
      group: 3,
      step: 8
    },

    {
      name: 'Documents',
      stageOutlet: 'documents',
      applicationStatus: 'Documents',
      isGroup: false,
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Single),
      showInProgress: () => true,
      mobileGroup: 5,
      group: 4,
      step: 9
    },

    {
      name: 'Documents',
      stageOutlet: 'documents-first-holder',
      applicationStatus: 'DocumentsFirst',
      isGroup: true,
      groupId: 4,
      groupName: 'FirstHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 5,
      group: 4,
      step: 9
    },

    {
      name: 'Documents',
      stageOutlet: 'documents-second-holder',
      applicationStatus: 'DocumentsSecond',
      isGroup: true,
      groupId: 5,
      isDelimiter: true,
      groupName: 'SecondHolder',
      canNavigate: () => this.accountTypeIs(this._userModel, AccountType.Joint, false),
      showInProgress: () => true,
      mobileGroup: 5,
      group: 4,
      step: 9
    },
  ];

  get workflowStages(): Stage[] {
    return this._workflowStages.filter(x => x.canNavigate());
  }

  get canNext(): boolean {
    return this._currentTabIndex <= this.workflowStages.length;
  }

  get canPrevious(): boolean {
    return this._currentTabIndex > 0;
  }

  get isMobile(): boolean {
    return window.innerWidth < 1200;
  }

  get progressWorkflowStages(): Stage[] {
    return this.workflowStages.filter(x => x.showInProgress() && x.canNavigate());
  }

  get displayProgressWorkflowStages(): Stage[] {
    return this.progressWorkflowStages.filter((x, i) => i <= this.currentDisplayTabIndex);
  }

  get groups(): number[] {
    return Array.from(
      new Set(
        this._workflowStages
          .filter(stage => stage.showInProgress())
          .map(stage => stage.group)
      )
    );
  }

  get currentGroupNumber(): number {
    return this.currentTab ? this.currentTab.group || 1 : 1;
  }

  get totalGroups(): number {
    return this.groups.length;
  }

  get overallProgress(): number {
    const currentStage = this.currentTab;
    if (!currentStage) {
      return 0;
    }

    const currentGroup = currentStage.group;
    const totalGroups = this.totalGroups;

    const completedGroups = currentGroup - 1;

    const groupStages = this._workflowStages
      .filter(s => s.group === currentGroup && s.showInProgress() && s.canNavigate());

    const currentIndex = groupStages.findIndex(s => s.applicationStatus === currentStage.applicationStatus);
    const groupProgress = currentIndex >= 0 ? (currentIndex + 1) / groupStages.length : 0;

    return ((completedGroups + groupProgress) / totalGroups) * 100;
  }

  get displayGroupStages() {
    const displayStages = this.workflowStages.filter((x, i) => x.isGroup && i <= this.currentTabIndex);

    const grouped = displayStages.reduce((accumulator, currentValue) => {
      const key = currentValue.groupId;

      if (!accumulator[key]) {
        accumulator[key] = new Array<Stage>();
      }

      accumulator[key].push(currentValue);

      return accumulator;
    }, {});

    return Object.keys(grouped).map(x => {
      const stages = grouped[x] as Stage[];
      const name = stages[0].groupName;

      const startStage = stages.reduce((min, current) => {
        return this.progressWorkflowStages.indexOf(current) < this.progressWorkflowStages.indexOf(min) ? current : min;
      }, stages[0]);

      const endStage = stages.reduce((min, current) => {
        return this.progressWorkflowStages.indexOf(current) > this.progressWorkflowStages.indexOf(min) ? current : min;
      }, stages[0]);

      return {
        name,
        start: this.progressWorkflowStages.indexOf(startStage) + 1,
        end: this.progressWorkflowStages.indexOf(endStage) + 2
      };
    });
  }

  get mobileStages(): number[] {
    const displayStages = this.workflowStages.filter((x, i) => x.showInProgress());

    const grouped = displayStages.reduce((accumulator, currentValue) => {
      const key = currentValue.mobileGroup;

      if (!accumulator[key]) {
        accumulator[key] = new Array<Stage>();
      }

      accumulator[key].push(currentValue);

      return accumulator;
    }, {});

    return Object.keys(grouped).map(x => +x);
  }

  getGroupStatus(groupNumber: number): 'completed' | 'active' | 'inactive' {
    const currentStage = this.currentTab;
    if (!currentStage) {
      return 'inactive';
    }

    const currentGroup = currentStage.group;

    if (currentGroup > groupNumber) {
      return 'completed';
    }

    if (currentGroup === groupNumber) {
      return 'active';
    }

    return 'inactive';
  }

  getGroupProgress(groupNumber: number): number {
    const currentStage = this.currentTab;
    if (!currentStage) {
      return 0;
    }

    const currentGroup = currentStage.group;

    if (currentGroup > groupNumber) {
      return 100;
    }

    if (currentGroup < groupNumber) {
      return 0;
    }

    const groupStages = this._workflowStages
      .filter(s => s.group === groupNumber && s.showInProgress() && s.canNavigate());

    const currentIndex = groupStages.findIndex(s => s.applicationStatus === currentStage.applicationStatus);

    if (currentIndex === -1) {
      return 0;
    }

    return ((currentIndex + 1) / groupStages.length) * 100;
  }

  next(): Stage {
    return this.workflowStages.find((x, i) => i > this._currentTabIndex && x.canNavigate());
  }

  previous(): Stage {
    return this.workflowStages.slice().reverse().find(x => this.workflowStages.indexOf(x) < this._currentTabIndex &&
      x.canNavigate() && x.splash !== true);
  }

  isCurrentTab(stage: Stage): boolean {
    return this.currentTab === stage;
  }

  isStage(outlets: string[]) {
    return this.currentTab && outlets.includes(this.currentTab.stageOutlet);
  }

  private updateCurrentTab(route: ActivatedRoute) {
    const outlet = this.getOutlet(route);

    if (this.workflowStages.some(st => st.stageOutlet === outlet)) {
      this._currentTab = this.workflowStages.find(st => st.stageOutlet === outlet);
      this._currentTabIndex = this.workflowStages.indexOf(this._currentTab);
      this._currentDisplayTabIndex = this.progressWorkflowStages.indexOf(this._currentTab);
    }

    this.onChange.emit();
  }

  private getOutlet(route: ActivatedRoute): string {
    let child = route.firstChild;
    while (child.firstChild !== null) {
      child = child.firstChild;
    }

    return child.routeConfig.path;
  }

  private accountTypeIs(userModel: User, accountType: AccountType, defaultValue = true) {
    if (!userModel) {
      return defaultValue;
    }

    return userModel.accountType === accountType;
  }

  private isEmployment(employment: IEmploymentInfo) {
    return employment &&
      (employment.employmentStatus === 'EMPLOYED' ||
        employment.employmentStatus === 'SELFEMPLOYED');
  }
}
